import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Filter, SearchService } from '@docpath/linces-angular-components/common';
import { DpHeaderModule, DpHeaderOption, DpSearchConfig, DpSearchModule, HeaderAction, SearchBasicInfo, SearchRowInfo } from '@docpath/webcomponents';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PageHeaderActionsService } from 'src/app/shared/components/header/services/page-header-actions.service';

import { AlertRule } from '../../../configuration/alert-rules/models/alert-rule';
import { Alert } from '../../models/alert';
import { MonitoringService } from '../../services/monitoring.service';
import { MonitoringTableService } from '../table/monitoring-table.service';
import { MonitoringHeaderService } from './monitoring-header.service';



@Component({
  selector: 'app-monitoring-header',
  templateUrl: './monitoring-header.component.html',
  standalone: true,
  imports: [DpHeaderModule, DpSearchModule]
})
export class MonitoringHeaderComponent implements OnInit, OnDestroy {
  @Input() alerts: Alert[] = [];
  @Output() filtersChanged = new EventEmitter<Filter[]>();
  @Output() columnVisibilityChanged = new EventEmitter<DpHeaderOption>();

  rightActions: HeaderAction[] = [];
  leftActions: HeaderAction[] = [];

  alertRules: AlertRule[] = [];

  showSearch = false;
  searchConfig = new DpSearchConfig({ resetOnClose: true, showMinimizeButton: true });

  private filters: Filter[] = [];
  private translateSubscription?: Subscription;

  @Input() dgeId!: number;

  searchPossibleFilters: SearchRowInfo[] = [];
  searchSelectedFilters: SearchRowInfo[] = [];
  searchAvailableOptions: SearchRowInfo[] = [];

  private readonly monitoringHeaderService = inject(MonitoringHeaderService);
  private readonly monitoringTableService = inject(MonitoringTableService);
  private readonly monitoringService = inject(MonitoringService);
  private readonly searchService = inject(SearchService);
  private readonly translateService = inject(TranslateService);
  private readonly pageHeaderActionsService = inject(PageHeaderActionsService);

  constructor() {
    this.searchAvailableOptions = this.monitoringHeaderService.initSearchAvailableOptions();
    this.rightActions = this.monitoringHeaderService.getRightActions();
    const searchAction = this.monitoringHeaderService.getSearchAction(this.rightActions);

    searchAction.action = () => {
      this.showSearch = !this.showSearch;
      this.updateSearchStyle();
    };
  }

  ngOnInit(): void {
    this.monitoringService.dgeChanged.subscribe(dgeId => {
      this.dgeId = dgeId;
      this.rightActions = this.monitoringHeaderService.updateRightActions(this.dgeId, this.rightActions);
      this.updateSearchStyle();
    });

    if (!this.translateSubscription) {
      this.translateSubscription = this.translateService.onLangChange.subscribe(() => {
        this.updateSearchStyle();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
  }

  isSearchVisible(): boolean {
    return this.showSearch;
  }

  clearSearch(): void {
    this.filters = [];
    this.filtersChanged.emit(this.filters);
    this.monitoringTableService.loadAlerts(this.dgeId, this.filters, 1);
    this.updateSearchStyle(0);
  }


  sendSearch(currentFilters: Array<SearchBasicInfo>): void {
    this.filters = this.searchService.toFilters(currentFilters);
    this.filtersChanged.emit(this.filters);
    this.monitoringTableService.loadAlerts(this.dgeId, this.filters, 1);
    this.updateSearchStyle();
  }

  hideSearch(): void {
    this.showSearch = false;
  }

  private updateSearchStyle(filtersCount?: number): void {
    const filterKeys = new Set(this.filters.map(filter => filter.key)),
      count = filtersCount ?? filterKeys.size;

    this.pageHeaderActionsService.updateSearchStyle(this.rightActions, count, 'search');
  }

  onChecklistOptionChanged(option: DpHeaderOption): void {
    const selectedFilter = this.searchSelectedFilters.find(filter => filter.key === option.value),
      possibleFilter = this.searchPossibleFilters.find(filter => filter.key === option.value);

    if (selectedFilter) {
      selectedFilter.visible = option.checked;
    }

    if (possibleFilter) {
      possibleFilter.visible = option.checked;
    }

    this.columnVisibilityChanged.emit(option);
  }


  onSelectedFiltersInitialized(filters: SearchRowInfo[]): void {
    this.searchSelectedFilters = filters;
  }

  onPossibleFiltersInitialized(filters: SearchRowInfo[]): void {
    this.searchPossibleFilters = filters;
  }


}
